import React from "react";
import ReactCountryFlag from "react-country-flag";
import { FaBolt, FaBrain, FaBuilding, FaBullhorn, FaCalendarDays, FaCode, FaEnvelope, FaFire, FaGlobe, FaLaptopCode, FaLocationDot, FaMobileScreenButton, FaPhone, FaSeedling, FaSnowflake, FaUserTie } from "react-icons/fa6";
import { hasUsableWebsite, scoreLabel, worthAssessment } from "../appShared";

const COUNTRY_CODE_MAP = {
  "Norway": "NO",
  "United Kingdom": "GB",
  "UK": "GB",
  "Denmark": "DK",
  "India": "IN",
  "UAE": "AE",
  "United Arab Emirates": "AE",
  "USA": "US",
  "US": "US",
  "United States": "US",
  "Singapore": "SG",
  "Australia": "AU",
  "Canada": "CA",
  "Germany": "DE",
  "Netherlands": "NL",
  "Sweden": "SE",
  "Saudi Arabia": "SA",
  "South Africa": "ZA",
};

const SOURCE_TYPES = [
  { match: (s) => s.includes("doffin") || s.includes("tender") || s.includes("contracts"), label: "TENDER",   cls: "src-tender",   accent: "#2563eb" },
  { match: (s) => s.includes("opportunit"),                                                  label: "OPPORT.",  cls: "src-opp",     accent: "#06b6d4" },
  { match: (s) => s.includes("norway") || s.includes("united kingdom") || s.includes("denmark"), label: "REGISTRY", cls: "src-registry", accent: "#3b82f6" },
  { match: ()  => true,                                                                       label: "MAPS",     cls: "src-maps",    accent: "#8b5cf6" },
];

function getSourceType(lead) {
  const s = String(lead?.source || "").toLowerCase();
  return SOURCE_TYPES.find((t) => t.match(s)) || SOURCE_TYPES[SOURCE_TYPES.length - 1];
}

const CATEGORY_TYPES = [
  {
    key: "ai",
    match: (s) => /\bai\b|artificial intelligence|machine learning|\bml\b|data science|chatbot|computer vision|neural network|kunstig intelligens/.test(s),
    label: "AI & ML",
    icon: FaBrain,
    cls: "category-ai",
  },
  {
    key: "mobile",
    match: (s) => /mobile app|\bios\b|\bandroid\b|app development|flutter|react native|mobilapp|app-utvikling/.test(s),
    label: "MOBILE APP",
    icon: FaMobileScreenButton,
    cls: "category-mobile",
  },
  {
    key: "webdev",
    match: (s) => /web development|website|web design|frontend|front-end|wordpress|shopify|web agency|\bweb dev\b|nettside|webdesign|webutvikling/.test(s),
    label: "WEB DEV",
    icon: FaCode,
    cls: "category-webdev",
  },
  {
    key: "softwaredev",
    match: (s) => /software development|software company|software solutions|custom software|programvare|programvareutvikling|dataprogrammering/.test(s),
    label: "SOFTWARE DEV",
    icon: FaLaptopCode,
    cls: "category-software",
  },
  {
    key: "consulting",
    match: (s) => /it consulting|\bconsulting\b|systems integration|managed services|it services|cybersecurity|cloud services|it-konsulent|konsulentselskap|it-tjenester/.test(s),
    label: "IT CONSULTING",
    icon: FaUserTie,
    cls: "category-consulting",
  },
  {
    key: "agency",
    match: (s) => /digital agency|marketing agency|branding|advertising|seo agency|creative agency|reklamebyra|digitalbyra|markedsforing/.test(s),
    label: "AGENCY",
    icon: FaBullhorn,
    cls: "category-agency",
  },
];

export function getLeadCategory(lead) {
  const text = [lead?.industry, lead?.name, lead?.description, lead?.summary, lead?.source]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const found = CATEGORY_TYPES.find((t) => t.match(text));
  if (found) return found;
  return { key: "other", label: "OTHER", icon: FaBuilding, cls: "category-other" };
}

function getIntentTag(lead) {
  const src  = String(lead?.source      || "").toLowerCase();
  const desc = String(lead?.description || lead?.summary || "").toLowerCase();
  const isTender  = src.includes("doffin") || src.includes("tender") || src.includes("contracts");
  const noWebsite = lead?.websiteStatus === "missing" || !lead?.website;
  const hasGrowth = desc.includes("hiring") || desc.includes("expansion") || desc.includes("growth") || desc.includes("partner");
  if (isTender)  return { label: "ACTIVE TENDER", cls: "intent-tender" };
  if (noWebsite) return { label: "NO WEBSITE",    cls: "intent-noweb"  };
  if (hasGrowth) return { label: "GROWING",        cls: "intent-grow"   };
  return null;
}

function getCountryCode(country) {
  return COUNTRY_CODE_MAP[String(country || "").trim()] || "";
}

function formatDeadline(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    const daysLeft = (d - Date.now()) / 86400000;
    if (daysLeft < 0) return "Closed";
    if (daysLeft < 1) return "Due today";
    if (daysLeft < 2) return "Due tomorrow";
    if (daysLeft <= 7) return `Due in ${Math.ceil(daysLeft)}d`;
    return `Due ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  } catch { return null; }
}

function isDeadlineUrgent(dateStr) {
  if (!dateStr) return false;
  try {
    const daysLeft = (new Date(dateStr) - Date.now()) / 86400000;
    return daysLeft > 0 && daysLeft <= 14;
  } catch { return false; }
}

function formatLeadNameForCard(lead, viewMode) {
  const raw = String(lead?.name || "").replace(/\s+/g, " ").trim();
  if (!raw) return "Unnamed Lead";

  const src = String(lead?.source || "").toLowerCase();
  const isTender = src.includes("doffin") || src.includes("tender") || src.includes("contracts");

  if (!isTender) return raw;

  // Keep original value in data; only compact long tender titles in card views.
  const max = viewMode === "list" ? 72 : 38;
  if (raw.length <= max) return raw;
  return `${raw.slice(0, max - 1).trimEnd()}...`;
}

export default function LeadCard({ lead, selected, onClick, viewMode, searching }) {
  const { label, emoji, color } = scoreLabel(lead.score || 0);
  const worth    = worthAssessment(lead);
  const countryCode = getCountryCode(lead.country);
  const srcType  = getSourceType(lead);
  const intentTag = getIntentTag(lead);
  const categoryTag = getLeadCategory(lead);
  const CategoryIcon = categoryTag.icon;
  const displayName = formatLeadNameForCard(lead, viewMode);
  const statusColors = { New: "#94a3b8", Contacted: "#3b82f6", Interested: "#f59e0b", Won: "#10b981", Lost: "#ef4444" };
  const isTender = srcType.cls === "src-tender";
  const cardScoreColor = isTender
    ? (lead.score >= 80 ? "#2563eb" : lead.score >= 60 ? "#0284c7" : lead.score >= 40 ? "#0f766e" : "#64748b")
    : color;

  if (viewMode === "list") {
    return (
      <div className={`lead-row status-${(lead.status || "New").toLowerCase()} ${selected ? "selected" : ""}`} onClick={onClick}>
        <div className="row-score" style={{ color: cardScoreColor }}>
          <span className="row-score-num">{lead.score}</span>
          {emoji === "fire" ? <FaFire /> : emoji === "bolt" ? <FaBolt /> : emoji === "seedling" ? <FaSeedling /> : <FaSnowflake />}
        </div>
        <div className="row-main">
          <div className="row-name" title={lead.name || ""}>
            {countryCode && (
              <span className="card-flag-chip" title={lead.country || "Country"}>
                <ReactCountryFlag
                  countryCode={countryCode}
                  svg
                  aria-label={lead.country || countryCode}
                  className="card-flag-icon"
                  style={{ width: "16px", height: "12px" }}
                />
              </span>
            )}
            {displayName}
          </div>
          <div className="row-meta">
            {isTender
              ? [lead.country, lead.tenderBuyer, lead.tenderDeadline && `Due: ${formatDeadline(lead.tenderDeadline)}`].filter(Boolean).join(" · ")
              : [lead.city, lead.country, lead.industry].filter(Boolean).join(" · ")
            }
          </div>
        </div>
        <div className="row-tags">
          <span className={`category-badge ${categoryTag.cls}`} data-tooltip-id="tt" data-tooltip-content={categoryTag.label}>
            <CategoryIcon />
          </span>
          <span className={`tag tag-src ${srcType.cls}`}>{srcType.label}</span>
          {intentTag && <span className={`tag tag-intent ${intentTag.cls}`}>{intentTag.label}</span>}
          {hasUsableWebsite(lead) && <span className="tag tag-web" data-tooltip-id="tt" data-tooltip-content={`Website: ${lead.website}`}><FaGlobe /></span>}
          {lead.email && <span className="tag tag-email" data-tooltip-id="tt" data-tooltip-content={`Email: ${lead.email}`}><FaEnvelope /></span>}
          {lead.phone && <span className="tag tag-phone" data-tooltip-id="tt" data-tooltip-content={`Phone: ${lead.phone}`}><FaPhone /></span>}
          <span className={`tag tag-worth tag-worth-${worth.key}`} data-tooltip-id="tt" data-tooltip-content={worth.reason}>{worth.label}</span>
          <span className="tag" style={{ background: statusColors[lead.status] + "22", color: statusColors[lead.status] }}>{lead.status}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`lead-card status-${(lead.status || "New").toLowerCase()} ${selected ? "selected" : ""}`} onClick={onClick}>
      {/* Coloured accent bar tied to source type */}
      <div className="card-top-accent" style={{ background: srcType.accent }} />

      {/* Header: company name + score badge */}
      <div className="card-header">
        <div className="card-flag-name">
          <div className="card-name" title={lead.name || ""}>{displayName}</div>
          <span className={`category-badge ${categoryTag.cls}`} data-tooltip-id="tt" data-tooltip-content={categoryTag.label}>
            <CategoryIcon />
            <span className="category-badge-label">{categoryTag.label}</span>
          </span>
        </div>
        <div className="card-score" style={{ background: cardScoreColor + "18", color: cardScoreColor }} data-tooltip-id="tt" data-tooltip-content={`${label} qualification — score ${lead.score}/100`}>
          <span>{lead.score}</span>
          <span className="card-score-label">SCORE</span>
        </div>
      </div>

      {/* Body: location + contextual info */}
      <div className="card-body">
        <div className="card-location"><FaLocationDot style={{ marginRight: 4 }} />{[lead.city, lead.country].filter(Boolean).join(", ") || (lead.country || "Unknown location")}</div>
        {isTender ? (
          <>
            {lead.tenderBuyer && (
              <div className="card-buyer" title={lead.tenderBuyer}>
                <FaBuilding />
                {lead.tenderBuyer.length > 32 ? lead.tenderBuyer.slice(0, 31) + "\u2026" : lead.tenderBuyer}
              </div>
            )}
            {lead.tenderDeadline && (
              <div className={`card-deadline${isDeadlineUrgent(lead.tenderDeadline) ? " card-deadline-urgent" : ""}`}>
                <FaCalendarDays />{formatDeadline(lead.tenderDeadline)}
              </div>
            )}
          </>
        ) : (
          <div className="card-industry">{lead.industry || lead.source || "Business lead"}</div>
        )}
      </div>

      {/* Footer: left = source + intent tags  |  right = status + contact signals + worth */}
      <div className="card-footer">
        <div className="card-tag-row">
          <span className={`tag tag-src ${srcType.cls}`}>{srcType.label}</span>
          {!isTender && intentTag && <span className={`tag tag-intent ${intentTag.cls}`}>{intentTag.label}</span>}
          <span className={`tag tag-worth tag-worth-${worth.key}`} data-tooltip-id="tt" data-tooltip-content={worth.reason}>{worth.label}</span>
        </div>
        <div className="card-right-tags">
          <span className="tag tag-status-chip" style={{ background: statusColors[lead.status || "New"] + "22", color: statusColors[lead.status || "New"] }}>
            {lead.status || "New"}
          </span>
          {!isTender && hasUsableWebsite(lead) && <span className="tag tag-web" data-tooltip-id="tt" data-tooltip-content={`Website: ${lead.website}`}><FaGlobe /></span>}
          {!isTender && lead.email  && <span className="tag tag-email" data-tooltip-id="tt" data-tooltip-content={`Email: ${lead.email}`}><FaEnvelope /></span>}
          {!isTender && lead.phone  && <span className="tag tag-phone" data-tooltip-id="tt" data-tooltip-content={`Phone: ${lead.phone}`}><FaPhone /></span>}
        </div>
      </div>

      {/* Score bar */}
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${lead.score}%`, background: cardScoreColor }} />
      </div>
    </div>
  );
}